import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { api } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { formatDateTime } from '../lib/utils';
import {
  countdownTarget,
  participantTone,
  PARTICIPANT_KEY,
  PHASE_KEY,
  phaseTone,
  RAIL_KEY,
  railTone,
} from '../lib/contest';
import { useAuth } from '../auth/AuthProvider';
import type { Contest, MyEntry } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { FullPageLoader } from '../components/ui/spinner';
import { Countdown } from '../components/controls/Countdown';
import { CryptoPaymentPanel } from '../components/crypto/CryptoPaymentPanel';

interface DetailPayload {
  contest: Contest;
  questionCount: number;
  answerKeyRevealed: boolean;
  nowMs: number;
}

/**
 * Contest detail and entry.
 *
 * Entry is a single POST that the server makes idempotent, so a double-click
 * cannot create two enrolments or two invoices. The response tells the page
 * exactly what state the participant is in — including whether online payment is
 * even configured — rather than the page guessing.
 */
export default function ContestDetailPage() {
  const { id = '' } = useParams();
  const { t, locale } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [contest, setContest] = useState<Contest | null>(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [serverTime, setServerTime] = useState('');
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const [entry, setEntry] = useState<MyEntry | null>(null);
  const [providerNote, setProviderNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const detail = await api.get<DetailPayload>(`/contests/${id}`);
      setContest(detail.contest);
      setQuestionCount(detail.questionCount);
      setServerTime(detail.contest.serverTime);
      setFetchedAt(Date.now());

      setEntry(isAuthenticated ? await api.get<MyEntry>(`/contests/${id}/me`) : null);
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleEnter(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ providerNote: string | null }>(`/contests/${id}/register`);
      setProviderNote(result.providerNote);
      await load();
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <FullPageLoader label={t('common.loading')} />;
  }

  if (!contest) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <Alert tone="error">{error ?? t('contest.detail.notFound')}</Alert>
        <div className="mt-6">
          <Link to="/contests">
            <Button variant="outline">{t('nav.contests')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const countdown = countdownTarget(contest);
  const participant = entry?.participant ?? null;
  const payment = entry?.payment ?? null;
  const freeEntry = contest.entryFee === '0';

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm">
        <Link to="/contests" className="text-muted hover:text-fg">
          {t('nav.contests')}
        </Link>
      </nav>

      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={railTone(contest.rail)}>{t(RAIL_KEY[contest.rail])}</Badge>
          <Badge tone={phaseTone(contest.phase)}>{t(PHASE_KEY[contest.phase])}</Badge>
          {participant && (
            <Badge tone={participantTone(participant.status)}>
              {t(PARTICIPANT_KEY[participant.status])}
            </Badge>
          )}
        </div>

        <h1 className="text-2xl font-semibold tracking-tight text-fg">{contest.title}</h1>
        {contest.summary && <p className="text-sm text-muted">{contest.summary}</p>}
      </header>

      {error && (
        <div className="mt-6">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      {contest.phase === 'cancelled' && (
        <div className="mt-6">
          <Alert tone="error">{t('contest.detail.cancelled')}</Alert>
        </div>
      )}
      {contest.phase === 'voided' && (
        <div className="mt-6">
          <Alert tone="error">{t('contest.detail.voided')}</Alert>
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>{t('contest.detail.about')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 text-sm">
            {contest.description && (
              <p className="whitespace-pre-line text-fg">{contest.description}</p>
            )}
            {contest.rules && (
              <div>
                <h2 className="mb-1 text-sm font-semibold text-fg">{t('contest.detail.rules')}</h2>
                <p className="whitespace-pre-line text-muted">{contest.rules}</p>
              </div>
            )}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('contest.detail.entryFee')}
                </dt>
                <dd className="text-fg">
                  {freeEntry ? t('contest.card.free') : `${contest.entryFee} ${contest.asset}`}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('contest.detail.prizePool')}
                </dt>
                <dd className="text-fg">
                  {contest.prizePool} {contest.asset}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('contest.detail.asset')}
                </dt>
                <dd className="text-fg">{contest.asset}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('contest.detail.questions')}
                </dt>
                <dd className="text-fg">{questionCount}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('contest.detail.paidPlaces')}
                </dt>
                <dd className="text-fg">{contest.paidPlaces}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('contest.detail.minParticipants')}
                </dt>
                <dd className="text-fg">{contest.minParticipants}</dd>
              </div>
            </dl>

            <div className="flex flex-col gap-1 border-t border-border pt-3">
              <p className="text-sm text-muted">
                {t('contest.card.startsAt', { date: formatDateTime(contest.startsAt, locale) })}
              </p>
              <p className="text-sm text-muted">
                {t('contest.card.endsAt', { date: formatDateTime(contest.endsAt, locale) })}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="h-fit">
          <CardHeader>
            <CardTitle>{t('contest.detail.register')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {countdown && serverTime && (
              <Countdown
                label={t(countdown.labelKey)}
                targetMs={countdown.at}
                serverTime={serverTime}
                fetchedAtMs={fetchedAt}
                onExpire={() => void load()}
              />
            )}

            <p className="text-sm text-muted">
              {t('contest.card.participants', { count: contest.participantCount })}
            </p>

            {!isAuthenticated && (
              <Link to="/login" state={{ from: `/contests/${id}` }}>
                <Button fullWidth>{t('contest.detail.signInToEnter')}</Button>
              </Link>
            )}

            {isAuthenticated && !participant && (
              <Button onClick={handleEnter} loading={busy} fullWidth>
                {busy ? t('contest.detail.registering') : t('contest.detail.register')}
              </Button>
            )}

            {isAuthenticated && participant && (
              <div className="flex flex-col gap-3">
                <p className="text-sm font-medium text-fg">{t('contest.detail.registered')}</p>

                {/* Hosted-checkout rails (currently switched off for launch): the
                    participant is sent to the provider's page. Kept intact so the
                    rail can be re-enabled without rebuilding this screen. */}
                {contest.rail !== 'crypto' && payment?.status === 'pending' && payment.hostedPageUrl && (
                  <a href={payment.hostedPageUrl} target="_blank" rel="noreferrer noopener">
                    <Button variant="outline" fullWidth>
                      {t('contest.detail.payNow')}
                    </Button>
                  </a>
                )}

                {contest.rail !== 'crypto' &&
                  payment?.status === 'pending' &&
                  !payment.hostedPageUrl && (
                    <Alert tone="info">
                      {providerNote ?? t('contest.detail.providerNotConfigured')}
                    </Alert>
                  )}

                {contest.rail !== 'crypto' && payment?.status === 'confirmed' && (
                  <Alert tone="success">{t('contest.detail.paymentConfirmed')}</Alert>
                )}

                {entry?.canStart && (
                  <Button onClick={() => navigate(`/contests/${id}/play`)} fullWidth>
                    {t('contest.detail.start')}
                  </Button>
                )}

                {participant.status === 'active' && (
                  <Button onClick={() => navigate(`/contests/${id}/play`)} fullWidth>
                    {t('contest.detail.continue')}
                  </Button>
                )}

                {contest.phase === 'results_ready' && (
                  <Link to={`/contests/${id}/results`}>
                    <Button variant="outline" fullWidth>
                      {t('contest.detail.viewResults')}
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* A crypto entry shows the real on-chain invoice: deposit address, QR,
          confirmations and transaction — all driven by server-verified state. */}
      {contest.rail === 'crypto' && isAuthenticated && participant && (
        <div className="mt-6">
          <CryptoPaymentPanel contestId={id} onStatusChange={() => void load()} />
        </div>
      )}
    </div>
  );
}
